import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button, LoadingButton } from "ui/button";
import EmailDesignPreview from "containers/admin/clients/touchpoint/components/email-design-preview/email-design-preview";
import { SkeletonLoader } from "components/modals/create-parent-touchpoint-modal/components/skeleton-loader";
import { Dialog, DialogActions } from "ui/dialog";

import { useUpdateClientTheme } from "components/modals/update-client-theme-modal/hooks/use-update-client-theme";

import Icons from "assets";

/**
 * The column labels stay put while the two previews scroll beneath them, so the comparison owns its
 * own scroll box and fills the body's height rather than letting the whole body scroll.
 */
const COMPARISON_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
};

interface UpdateClientThemeModalProps {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  touchpointVersionId: string;
  clientId: string;
  campaignId: string;
  touchpointId: string;
}

const UpdateClientThemeModal = ({
  isOpen,
  setIsOpen = () => {},
  touchpointVersionId,
  clientId,
  campaignId,
  touchpointId,
}: UpdateClientThemeModalProps) => {
  const {
    touchpointData,
    currentTouchpointVersion,
    isLoadingExisting,
    isLoadingThemed,
    isSaving,
    existingDesignError,
    themeUpdatedError,
    isRetryableError,
    buttonState,
    handleApplyTheme,
    handleRetry,
  } = useUpdateClientTheme({
    touchpointVersionId,
    isOpen,
    setIsOpen,
    clientId,
    campaignId,
    touchpointId,
  });

  if (!isOpen) {
    return null;
  }

  const themeUpdatedErrorStyles = `flex items-center gap-2 mb-4 ${isRetryableError ? "text-gray-600" : "text-critical-700"}`;

  return (
    <Dialog
      open
      onClose={() => setIsOpen(false)}
      size="large"
      width={900}
      headline="Apply client theme"
      description="Please ensure the client theme is applied correctly."
      actions={
        <DialogActions
          primary={
            <LoadingButton
              size="large"
              state={buttonState}
              onClick={handleApplyTheme}
              loadingMessage="Applying theme..."
              successMessage="Theme applied"
              errorMessage="Failed to apply"
              disabled={
                isLoadingExisting ||
                isLoadingThemed ||
                !touchpointData.existingDesignHtml ||
                !touchpointData.themeUpdatedHtml ||
                !currentTouchpointVersion
              }>
              Apply theme
            </LoadingButton>
          }
          secondary={
            <Button
              size="large"
              variant="outlined"
              onClick={() => setIsOpen(false)}
              disabled={isLoadingExisting || isLoadingThemed || isSaving}>
              Cancel
            </Button>
          }
        />
      }>
      <Box sx={COMPARISON_SX}>
        <div className="flex justify-center gap-4 pr-3 pl-1 pb-1 flex-shrink-0 min-w-0">
          <h2
            className="flex-1 min-w-0 max-w-[400px] text-left text-sm text-gray-900"
            id="existing-design-header">
            Exisiting design
          </h2>
          <h2
            className="flex-1 min-w-0 max-w-[400px] text-left text-sm text-gray-900"
            id="theme-updates-header">
            Theme updates
          </h2>
        </div>
        <div className="flex-1 overflow-y-scroll custom-scroll min-h-0">
          <div className="flex justify-center gap-4 p-1 pt-0 min-w-0">
            <div
              className="flex-1 min-w-0 max-w-[400px] relative"
              aria-labelledby="existing-design-header">
              {isLoadingExisting && (
                <div className="flex items-center justify-center w-full min-h-full absolute inset-0">
                  <SkeletonLoader />
                </div>
              )}
              {!isLoadingExisting &&
                touchpointData.existingDesignHtml &&
                touchpointData.existingDesignBeeJson && (
                  <div className="shadow shadow-gray-100 relative overflow-hidden">
                    <EmailDesignPreview
                      htmlContent={touchpointData.existingDesignHtml}
                      htmlJSON={touchpointData.existingDesignBeeJson}
                      isCommentMode={false}
                      mobileView={true}
                    />
                  </div>
                )}
              {!isLoadingExisting && existingDesignError && (
                <div className="h-full relative border border-gray-200 shadow shadow-gray-100 bg-gray-50 p-6">
                  <div className="sticky top-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center p-4">
                    <div className="flex items-center gap-2 mb-4 text-critical-700">
                      <Icons.ExclamationCircle />
                      <h3>{existingDesignError}</h3>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className="flex-1 min-w-0 max-w-[400px] relative"
              aria-labelledby="theme-updates-header">
              {isLoadingThemed && (
                <div className="flex items-center justify-center w-full min-h-full absolute inset-0">
                  <SkeletonLoader />
                </div>
              )}
              {!isLoadingThemed &&
                touchpointData.themeUpdatedHtml &&
                touchpointData.themeUpdatedBeeJson && (
                  <div className="border border-aqua-600 shadow shadow-aqua-100 relative overflow-hidden">
                    <EmailDesignPreview
                      htmlContent={touchpointData.themeUpdatedHtml}
                      htmlJSON={touchpointData.themeUpdatedBeeJson}
                      isCommentMode={false}
                      mobileView={true}
                    />
                  </div>
                )}
              {!isLoadingThemed && themeUpdatedError && (
                <div className="h-full relative border border-gray-200 shadow shadow-gray-100 bg-gray-50 p-6">
                  <div className="sticky top-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center p-4">
                    <h3 className={themeUpdatedErrorStyles}>
                      <Icons.ExclamationCircle aria-hidden={true} />
                      {themeUpdatedError}
                    </h3>
                    {isRetryableError && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Icons.Replay className="scale-x-[-1]" />}
                        onClick={handleRetry}
                        disabled={isLoadingThemed}>
                        Retry applying
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Box>
    </Dialog>
  );
};

export default UpdateClientThemeModal;
